const GENERIC_FONT_FAMILIES = new Set([
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
  'ui-serif',
  'ui-sans-serif',
  'ui-monospace',
  'ui-rounded',
  'math',
  'emoji',
  'fangsong',
  'inherit',
  'initial',
  'revert',
  'revert-layer',
  'unset'
]);

/**
 * Named families that ship with an operating system and are published by NO
 * webfont CDN. They are usable font families — a template that asks for
 * Georgia gets Georgia — but asking Google Fonts or GWFH for one can only ever
 * produce a 400/404, and the builder was doing exactly that: `Menlo` and
 * `Consolas` came out of monospace stacks in crawled CSS and were requested on
 * every export and every canvas paint.
 *
 * Membership is EVIDENCE, not taxonomy: every name here was confirmed absent
 * from the GWFH catalog (2026-08-30). Families that merely feel system-ish but
 * are real webfonts — Ubuntu, Oxygen, Cantarell, Noto Sans, Roboto — are
 * deliberately NOT here, because excluding one would drop a font the site was
 * designed with.
 */
const SYSTEM_FONT_FAMILIES = new Set(
  [
    '-apple-system',
    'BlinkMacSystemFont',
    'SF Pro',
    'SF Pro Text',
    'SF Pro Display',
    'SF Mono',
    'SFMono-Regular',
    'Segoe UI',
    'Segoe UI Emoji',
    'Segoe UI Symbol',
    'Apple Color Emoji',
    'Helvetica',
    'Helvetica Neue',
    'Arial',
    'Arial Black',
    'Arial Narrow',
    'Times',
    'Times New Roman',
    'Courier',
    'Courier New',
    'Georgia',
    'Verdana',
    'Tahoma',
    'Trebuchet MS',
    'Impact',
    'Comic Sans MS',
    'Palatino',
    'Palatino Linotype',
    'Book Antiqua',
    'Garamond',
    'Baskerville',
    'Didot',
    'Optima',
    'Futura',
    'Gill Sans',
    'Geneva',
    'Century Gothic',
    'Perpetua',
    'Rockwell',
    'Cambria',
    'Calibri',
    'Candara',
    'Constantia',
    'Corbel',
    'Lucida Grande',
    'Lucida Sans',
    'Lucida Sans Unicode',
    'Lucida Console',
    'Menlo',
    'Monaco',
    'Consolas',
    'Andale Mono',
    'Liberation Mono',
    'Liberation Sans',
    'Liberation Serif',
    'DejaVu Sans',
    'DejaVu Serif',
    'DejaVu Sans Mono'
  ].map((name) => name.toLowerCase())
);

function stripWrappingQuotes(value) {
  let next = String(value || '').trim();
  while (next.length >= 2) {
    const first = next[0];
    const last = next[next.length - 1];
    if ((first !== '"' && first !== "'") || first !== last) break;
    next = next.slice(1, -1).trim();
  }
  return next;
}

function normalizeFontFamilyName(value) {
  let next = stripWrappingQuotes(value);
  if (!next) return '';
  next = next.replace(/\s+fallback\s*:\s*.+$/i, '').trim();
  next = next.replace(/-[a-f0-9]{10,}$/i, '').trim();
  return next;
}

/**
 * Parse the first font-family token from a CSS font-family value.
 */
export function parseFirstFont(cssValue) {
  if (!cssValue || typeof cssValue !== 'string') return null;
  const trimmed = cssValue.trim();
  if (!trimmed) return null;

  const quote = trimmed[0];
  if (quote === '"' || quote === "'") {
    let escaped = false;
    for (let i = 1; i < trimmed.length; i += 1) {
      const char = trimmed[i];
      if (escaped) {
        escaped = false;
        continue;
      }
      if (char === '\\') {
        escaped = true;
        continue;
      }
      if (char === quote) {
        return normalizeFontFamilyName(trimmed.slice(1, i)) || null;
      }
    }
  }

  const first = trimmed.split(',')[0];
  return normalizeFontFamilyName(first) || null;
}

export function isGenericFontFamily(name) {
  if (!name || typeof name !== 'string') return true;
  return GENERIC_FONT_FAMILIES.has(normalizeFontFamilyName(name).toLowerCase());
}

/**
 * True for a named family that resolves from the operating system and can
 * never be fetched. Deliberately NOT folded into `isUsableFontFamily` — these
 * families are perfectly usable, and treating them as unusable would make a
 * template that chose Georgia fall back to Roboto. This predicate answers only
 * "should we ask a CDN for it".
 */
export function isSystemFontFamily(name) {
  if (!name || typeof name !== 'string') return false;
  return SYSTEM_FONT_FAMILIES.has(normalizeFontFamilyName(name).toLowerCase());
}

export function isUsableFontFamily(name) {
  if (!name || typeof name !== 'string') return false;
  const normalized = normalizeFontFamilyName(name);
  if (!normalized || isGenericFontFamily(normalized)) return false;
  if (/[()]/.test(normalized)) return false;
  return true;
}

function firstUsableFont(...values) {
  for (const value of values) {
    const parsed = parseFirstFont(value);
    if (isUsableFontFamily(parsed)) return parsed;
  }
  return null;
}

export function parseGoogleFontFamiliesFromHref(href) {
  if (!href || typeof href !== 'string') return [];

  let url;
  try {
    url = new URL(href, 'https://fonts.googleapis.com');
  } catch {
    return [];
  }

  if (!url.hostname.includes('fonts.googleapis.com') || !url.pathname.includes('/css')) {
    return [];
  }

  const families = [];
  for (const rawFamily of url.searchParams.getAll('family')) {
    for (const part of rawFamily.split('|')) {
      const family = part.replace(/\+/g, ' ').split(':')[0].trim();
      const normalized = normalizeFontFamilyName(family);
      if (isUsableFontFamily(normalized)) families.push(normalized);
    }
  }
  return families;
}

export function extractTemplateFontChoices(template, themeVarsOverride = null) {
  const themeVars = themeVarsOverride || template?.themeVars || {};
  const headingFont = firstUsableFont(template?.declaredHeadingFont, themeVars['--headerFont']);
  const bodyFont = firstUsableFont(template?.declaredBodyFont, themeVars['--bodyFont']);
  const navFont = firstUsableFont(
    template?.declaredNavFont,
    themeVars['--navFont'],
    themeVars['--navNeon']
  );
  const fallbackFont = Array.isArray(template?.fontFamilies)
    ? firstUsableFont(...template.fontFamilies)
    : null;

  return {
    headingFont: headingFont || null,
    bodyFont: bodyFont || fallbackFont || 'Roboto',
    navFont: navFont || null,
    fallbackFont: fallbackFont || null
  };
}
